import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CalendarCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const INTERESTS = [
  { value: 'hiring', label: 'Hiring Organization' },
  { value: 'staffing', label: 'Staffing Organization' },
  { value: 'applicant', label: 'Applicant' },
];

export default function BookDemoModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', interest: 'hiring', message: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/demo-requests`, form);
      toast.success("Thanks! Our team will reach out shortly.");
      setForm({ name: '', email: '', company: '', interest: 'hiring', message: '' });
      onOpenChange(false);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-emerald-400 dark:focus:border-emerald-600 focus:ring-1 focus:ring-emerald-400/40 transition-colors';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-[#0d1117] border-slate-200 dark:border-zinc-800" data-testid="book-demo-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
            <CalendarCheck className="w-5 h-5 text-emerald-500" /> Book a demo
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-zinc-400">
            See how numuv&apos;s AI transforms your hiring journey.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input data-testid="demo-name-input" required minLength={2} placeholder="Your name" value={form.name} onChange={set('name')} className={inputCls} />
          <input data-testid="demo-email-input" required type="email" placeholder="Work email" value={form.email} onChange={set('email')} className={inputCls} />
          <input data-testid="demo-company-input" placeholder="Company (optional)" value={form.company} onChange={set('company')} className={inputCls} />
          <div className="grid grid-cols-3 gap-2">
            {INTERESTS.map((it) => (
              <button
                key={it.value}
                type="button"
                data-testid={`demo-interest-${it.value}`}
                onClick={() => setForm((f) => ({ ...f, interest: it.value }))}
                className={`px-2 py-2 rounded-lg border text-xs font-medium transition-all ${form.interest === it.value ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-slate-200 dark:border-zinc-800 text-slate-500 dark:text-zinc-400 hover:border-slate-300 dark:hover:border-zinc-600'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
          <textarea data-testid="demo-message-input" rows={3} placeholder="Tell us about your needs (optional)" value={form.message} onChange={set('message')} className={inputCls} />
          <button
            type="submit"
            disabled={loading}
            data-testid="demo-submit-btn"
            className="w-full bg-slate-900 dark:bg-white text-white dark:text-black font-medium py-2.5 rounded-lg hover:bg-slate-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Request demo'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
